import { Download, FileText, RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  useDownloadMonthlyReport,
  useGenerateMonthlyReport,
  useVesselMonthlyReports,
} from '@/features/monthly-reports/queries';
import {
  formatPeriod,
  statusBadgeClass,
  type MonthlyReport,
} from '@/features/monthly-reports/types';
import { useAuthStore } from '@/auth/store';
import { hasAnyRole } from '@/auth/types';
import { cn } from '@/lib/utils';

const PRIVILEGED_GENERATE: Array<'isi_admin' | 'superadmin' | 'fleet_manager'> = [
  'isi_admin',
  'superadmin',
  'fleet_manager',
];

/**
 * L19 — "Reports" card on VesselDetailPage. Shows the 6 most recent
 * reports + per-row Download / Regenerate buttons. The Regenerate
 * button is gated to admin / fleet manager (matches backend RBAC).
 */
export function VesselReportsCard({ vesselId }: { vesselId: string }) {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const canRegenerate = hasAnyRole(user, PRIVILEGED_GENERATE);

  const { data, isLoading, isError } = useVesselMonthlyReports(vesselId);
  const generateMut = useGenerateMonthlyReport(vesselId);
  const downloadMut = useDownloadMonthlyReport();

  const onGenerateLatest = () => {
    // Default: regenerate previous calendar month (matches the cron).
    const now = new Date();
    const periodMonth = now.getUTCMonth() === 0 ? 12 : now.getUTCMonth();
    const periodYear =
      now.getUTCMonth() === 0
        ? now.getUTCFullYear() - 1
        : now.getUTCFullYear();
    generateMut.mutate({ periodYear, periodMonth });
  };

  return (
    <Card data-testid="vessel-reports-card">
      <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0">
        <CardTitle className="flex items-center gap-2 text-base">
          <FileText aria-hidden className="h-4 w-4" />
          {t('monthlyReports.cardTitle')}
        </CardTitle>
        {canRegenerate && (
          <Button
            variant="outline"
            size="sm"
            onClick={onGenerateLatest}
            disabled={generateMut.isPending}
            data-testid="vessel-reports-regenerate"
          >
            <RefreshCw aria-hidden className="mr-1 h-4 w-4" />
            {t('monthlyReports.regenerateLatest')}
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {isError && (
          <Alert variant="destructive">
            <AlertTitle>{t('monthlyReports.loadError')}</AlertTitle>
          </Alert>
        )}
        {isLoading ? (
          <Skeleton className="h-24 w-full" />
        ) : !data || data.length === 0 ? (
          <p className="py-3 text-sm text-muted-foreground">
            {t('monthlyReports.empty')}
          </p>
        ) : (
          <ul className="space-y-2">
            {data.slice(0, 6).map((r) => (
              <ReportRow
                key={r.id}
                report={r}
                onDownload={() =>
                  downloadMut.mutate({
                    vesselId: r.vesselId,
                    periodYear: r.periodYear,
                    periodMonth: r.periodMonth,
                  })
                }
              />
            ))}
          </ul>
        )}
        {downloadMut.isError && (
          <Alert variant="destructive" className="mt-3">
            <AlertDescription>
              {t('monthlyReports.downloadError')}
            </AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}

function ReportRow({
  report,
  onDownload,
}: {
  report: MonthlyReport;
  onDownload: () => void;
}) {
  const { t } = useTranslation();
  const period = formatPeriod(report);
  const downloadable = report.status === 'READY' || report.status === 'SENT';
  return (
    <li
      className="flex items-center justify-between gap-2 rounded-md border p-2"
      data-testid={`report-row-${report.id}`}
    >
      <div className="flex flex-col">
        <span className="font-medium">{period}</span>
        <span className="text-xs text-muted-foreground">
          {report.sentAt
            ? t('monthlyReports.sentTo', {
                count: report.sentToEmails.length,
              })
            : new Date(report.generatedAt).toLocaleDateString()}
        </span>
      </div>
      <div className="flex items-center gap-2">
        <span
          className={cn(
            'inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium',
            statusBadgeClass(report.status),
          )}
        >
          {t(`monthlyReports.status.${report.status}`)}
        </span>
        <Button
          variant="outline"
          size="sm"
          disabled={!downloadable}
          onClick={onDownload}
          data-testid={`report-download-${report.id}`}
        >
          <Download aria-hidden className="mr-1 h-4 w-4" />
          {t('monthlyReports.download')}
        </Button>
      </div>
    </li>
  );
}
